import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { toast } from 'react-toastify';
import { Check, Copy, QrCode } from 'lucide-react';
import { ButtonLink } from 'components/Button/ButtonLink';
import { getTargetLtvDisplay, formatOptionalCollateralAmount } from 'containers/pages/Dashboard/utils/cryptoUtils';
import 'containers/pages/Dashboard/components/LowerLtvModal/style.scss';

const QuickPayCollateralDeposit = ({
  depositAmount,
  coinType,
  walletAddress,
  regularPaymentAmount,
  nextPaymentDueDate,
  pastDueAmount,
  showAmountBox,
}) => {
  const [isQrOpen, setIsQrOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const copiedTimeoutRef = useRef(null);
  const hasWalletAddress = Boolean(String(walletAddress || '').trim());
  const addressDisplay = hasWalletAddress ? walletAddress : '-';
  const { targetLtvDisplay: targetLtvLabel } = getTargetLtvDisplay();
  const coinLabel = coinType.toUpperCase();
  const depositDisplay = formatOptionalCollateralAmount(depositAmount, coinType);

  useEffect(() => () => {
    if (copiedTimeoutRef.current) {
      window.clearTimeout(copiedTimeoutRef.current);
    }
  }, []);

  const copyWalletToClipboard = async () => {
    if (!hasWalletAddress || isCopied) {
      return;
    }

    try {
      await navigator.clipboard.writeText(walletAddress);
      setIsCopied(true);
      if (copiedTimeoutRef.current) {
        window.clearTimeout(copiedTimeoutRef.current);
      }
      copiedTimeoutRef.current = window.setTimeout(() => setIsCopied(false), 3000);
    } catch {
      toast.error('Unable to copy address. Please copy it manually.');
    }
  };

  const qrImageUrl = hasWalletAddress
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(walletAddress)}`
    : '';

  const depositSubtext = `To return to ${targetLtvLabel} LTV.`;

  return (
    <div className="lower-ltv-modal__deposit">
      {showAmountBox && (
      <div className="lower-ltv-modal__deposit-box text-center">
        <small className="lower-ltv-modal__deposit-label">TRANSFER AMOUNT</small>
        <div className="lower-ltv-modal__deposit-amount">
          {`${depositAmount} ${coinLabel}`}
        </div>
        <p className="lower-ltv-modal__deposit-subtext mb-0">
          {depositSubtext}
        </p>
      </div>
      )}

      <div className="lower-ltv-modal__deposit-box">
        <div className="lower-ltv-modal__wallet-header">
          <small className="lower-ltv-modal__deposit-label mb-0">
            SEND TO WALLET ADDRESS
          </small>
          <div className="lower-ltv-modal__wallet-actions">
            <button
              type="button"
              className="lower-ltv-modal__wallet-action-btn lower-ltv-modal__wallet-action-btn--bordered lower-ltv-modal__wallet-action-btn--has-tooltip"
              onClick={copyWalletToClipboard}
              aria-label="Copy wallet address"
              disabled={!hasWalletAddress || isCopied}
            >
              <span className="lower-ltv-modal__copy-icon-wrap">
                <Copy
                  size={14}
                  aria-hidden="true"
                  className={`lower-ltv-modal__copy-icon ${isCopied ? 'lower-ltv-modal__copy-icon--hidden' : 'lower-ltv-modal__copy-icon--visible'}`}
                />
                <Check
                  size={14}
                  aria-hidden="true"
                  className={`lower-ltv-modal__copy-icon lower-ltv-modal__copy-icon--check ${isCopied ? 'lower-ltv-modal__copy-icon--visible' : 'lower-ltv-modal__copy-icon--hidden'}`}
                />
              </span>
              <span className="lower-ltv-modal__wallet-action-tooltip">Copy to clipboard</span>
            </button>
            <button
              type="button"
              className="lower-ltv-modal__wallet-action-btn lower-ltv-modal__wallet-action-btn--bordered lower-ltv-modal__wallet-action-btn--has-tooltip"
              onClick={() => setIsQrOpen(true)}
              aria-label="Show QR code"
              disabled={!hasWalletAddress}
            >
              <QrCode size={14} aria-hidden="true" />
              <span className="lower-ltv-modal__wallet-action-tooltip">View QR code</span>
            </button>
          </div>
        </div>
        <p className="lower-ltv-modal__wallet-address mb-0" style={{ textAlign: 'left' }}>{addressDisplay}</p>
      </div>

      <div className="lower-ltv-modal__deposit-instructions">
        <small className="lower-ltv-modal__deposit-label">TRANSFER INSTRUCTIONS</small>
        <ol className="lower-ltv-modal__instruction-list">
          <li>
            <span className="lower-ltv-modal__instruction-step">1</span>
            <span>
              {'Copy the address and send '}
              <strong>{depositDisplay}</strong>
              {' from your wallet'}
            </span>
          </li>
          <li>
            <span className="lower-ltv-modal__instruction-step">2</span>
            <span>
              {`Only send ${coinLabel}. Other assets will be `}
              <strong>permanently lost</strong>
            </span>
          </li>
          <li>
            <span className="lower-ltv-modal__instruction-step">3</span>
            <span>
              After sending, check the
              {' '}
              <strong>Collateral History</strong>
              {' '}
              section in the Portal to track your transfer
            </span>
          </li>
        </ol>
      </div>

      {(pastDueAmount || (regularPaymentAmount && nextPaymentDueDate)) && (
        <div className="lower-ltv-modal__payment-banner">
          {pastDueAmount
            ? (
              <>
                Depositing collateral reduces your LTV but
                {' '}
                <strong>{`does not cover your $${pastDueAmount} past due`}</strong>
                . Use the Make extra payment tab to cover both in one transaction.
              </>
            )
            : `Depositing collateral does not replace your regular $${regularPaymentAmount} payment due ${nextPaymentDueDate}.`}
        </div>
      )}

      <Modal isOpen={isQrOpen} toggle={() => setIsQrOpen(false)} centered size="sm" className="lower-ltv-qr-modal">
        <ModalHeader>
          Scan to send
          <button type="button" className="lower-ltv-qr-modal__close" onClick={() => setIsQrOpen(false)} aria-label="Close">
            &times;
          </button>
        </ModalHeader>
        <ModalBody className="lower-ltv-qr-modal__body">
          {hasWalletAddress && (
            <img
              src={qrImageUrl}
              alt={`QR code for ${coinLabel} deposit address`}
              className="lower-ltv-qr-modal__qr-image"
            />
          )}
          <p className="lower-ltv-qr-modal__address">{addressDisplay}</p>
          <ButtonLink
            className="lower-ltv-qr-modal__copy-btn"
            onClick={copyWalletToClipboard}
            disabled={!hasWalletAddress}
          >
            Copy address
          </ButtonLink>
        </ModalBody>
      </Modal>
    </div>
  );
};

QuickPayCollateralDeposit.propTypes = {
  depositAmount: PropTypes.string.isRequired,
  coinType: PropTypes.string.isRequired,
  walletAddress: PropTypes.string,
  regularPaymentAmount: PropTypes.string,
  nextPaymentDueDate: PropTypes.string,
  pastDueAmount: PropTypes.string,
  showAmountBox: PropTypes.bool,
};

QuickPayCollateralDeposit.defaultProps = {
  walletAddress: '',
  regularPaymentAmount: '',
  nextPaymentDueDate: '',
  pastDueAmount: '',
  showAmountBox: true,
};

export default QuickPayCollateralDeposit;
